/**
 * ThreadTagFilterBar — flag-gated controls that emit a `ThreadTagFilter` for
 * filtering a thread list by workflow tags. Controlled component: parent owns
 * `value` and applies the filter (via `filterThreadsByTags` / the service's
 * `listByFilter`). Renders `null` unless enabled.
 */
import React from 'react';
import { isGroupThreadTagsEnabled } from '../../services/threadTags/featureFlags';
import {
  DEFAULT_WORKFLOW_LABEL_ALLOWLIST,
  PRIORITY_LADDER,
  TAG_STATUSES,
  type TagPriority,
  type TagStatus,
} from '../../services/threadTags/taxonomy';
import type { ThreadTagFilter } from '../../services/threadTags/types';
import { TagFilterBar, TagFilterLabel, TagFilterSelect } from './ThreadTagChips.styles';

export interface OwnerOption {
  id: string;
  label: string;
}

export interface ThreadTagFilterBarProps {
  value: ThreadTagFilter;
  onChange: (next: ThreadTagFilter) => void;
  enabled?: boolean;
  /** When provided, an owner filter select is shown. */
  ownerOptions?: OwnerOption[];
}

const STATUS_TITLE: Record<TagStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  blocked: 'Blocked',
  waiting: 'Waiting',
  resolved: 'Resolved',
  archived: 'Archived',
};

const PRIORITY_TITLE: Record<TagPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

// `null` filter owner (unassigned) needs a sentinel distinct from "" (Anyone).
const UNASSIGNED = '__unassigned__';

export const ThreadTagFilterBar: React.FC<ThreadTagFilterBarProps> = ({
  value,
  onChange,
  enabled,
  ownerOptions,
}) => {
  if (!isGroupThreadTagsEnabled(enabled)) return null;

  const currentStatus = value.status && value.status.length === 1 ? value.status[0] : '';
  const currentLabel = value.labels && value.labels.length === 1 ? value.labels[0] : '';
  const currentOwner =
    value.ownerUserId === undefined ? '' : value.ownerUserId === null ? UNASSIGNED : value.ownerUserId;

  return (
    <TagFilterBar data-thread-tag-filter="">
      <TagFilterLabel>Filter</TagFilterLabel>

      <TagFilterSelect
        aria-label="filter by status"
        data-thread-tag-filter-status=""
        value={currentStatus}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ ...value, status: v ? [v as TagStatus] : undefined });
        }}
      >
        <option value="">Any status</option>
        {TAG_STATUSES.map((s) => (
          <option key={s} value={s}>
            {STATUS_TITLE[s]}
          </option>
        ))}
      </TagFilterSelect>

      <TagFilterSelect
        aria-label="filter by minimum priority"
        data-thread-tag-filter-priority=""
        value={value.minPriority ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ ...value, minPriority: v ? (v as TagPriority) : undefined });
        }}
      >
        <option value="">Any priority</option>
        {PRIORITY_LADDER.map((p) => (
          <option key={p} value={p}>
            {PRIORITY_TITLE[p]}+
          </option>
        ))}
      </TagFilterSelect>

      <TagFilterSelect
        aria-label="filter by label"
        data-thread-tag-filter-label=""
        value={currentLabel}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ ...value, labels: v ? [v] : undefined });
        }}
      >
        <option value="">Any label</option>
        {DEFAULT_WORKFLOW_LABEL_ALLOWLIST.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </TagFilterSelect>

      {ownerOptions && ownerOptions.length > 0 && (
        <TagFilterSelect
          aria-label="filter by owner"
          data-thread-tag-filter-owner=""
          value={currentOwner}
          onChange={(e) => {
            const v = e.target.value;
            const ownerUserId = v === '' ? undefined : v === UNASSIGNED ? null : v;
            onChange({ ...value, ownerUserId });
          }}
        >
          <option value="">Anyone</option>
          <option value={UNASSIGNED}>Unassigned</option>
          {ownerOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </TagFilterSelect>
      )}
    </TagFilterBar>
  );
};

export default ThreadTagFilterBar;
